// src/components/CouponDialog.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import {
  AdminCoupon,
  createAdminCoupon,
  getAdminCoupon,
  updateAdminCoupon,
} from "../api/adminCouponsApi";
import { http } from "../api/http";

type Props = {
  open: boolean;
  couponId: number | null;
  onClose: () => void;
  onSaved: () => void;
};

type SimpleOption = { id: number; label: string };

async function fetchAllProducts(): Promise<SimpleOption[]> {
  const res = await http.get<any[]>(`/api/products`);
  const rows = Array.isArray(res.data) ? res.data : [];

  return rows
    .map((p) => ({
      id: Number(p.product_id ?? p.id),
      label: String(p.product_name ?? p.name ?? "").trim(),
    }))
    .filter((p) => Number.isFinite(p.id) && p.label.length > 0);
}


async function fetchAllCategories(): Promise<SimpleOption[]> {
  const res = await http.get<any>(`/categories`);
  const rows = Array.isArray(res.data) ? res.data : res.data?.categories || [];

  return rows
    .map((c: any) => ({
      id: Number(c.id),
      label: String(c.name ?? "").trim(),
    }))
    .filter((c: any) => Number.isFinite(c.id) && c.label.length > 0);
}

const CouponDialog: React.FC<Props> = ({ open, couponId, onClose, onSaved }) => {
  const isEdit = !!couponId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [products, setProducts] = useState<SimpleOption[]>([]);
  const [categories, setCategories] = useState<SimpleOption[]>([]);

  const [form, setForm] = useState<any>({
    code: "",
    discount_type: "percentage",
    discount_value: 10,
    max_discount: "",
    channel: "online",
    applies_to: "all",
    starts_at: "",
    ends_at: "",
    usage_limit: "",
    usage_per_user_limit: "",
    min_subtotal: "",
    is_active: true,
    notes: "",
    product_ids: [] as number[],
    category_ids: [] as number[],
  });

  const selectedProducts = useMemo(
    () => products.filter((p) => form.product_ids.includes(p.id)),
    [products, form.product_ids]
  );

  const selectedCategories = useMemo(
    () => categories.filter((c) => form.category_ids.includes(c.id)),
    [categories, form.category_ids]
  );

  const load = async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const [prods, cats] = await Promise.all([fetchAllProducts(), fetchAllCategories()]);
      setProducts(prods);
      setCategories(cats);

      if (couponId) {
        const c: AdminCoupon = await getAdminCoupon(couponId);
        setForm({
          code: c.code || "",
          discount_type: c.discount_type,
          discount_value: Number(c.discount_value || 0),
          max_discount: c.max_discount ?? "",
          channel: c.channel,
          applies_to: c.applies_to,
          starts_at: c.starts_at ? dayjs(c.starts_at).format("YYYY-MM-DDTHH:mm") : "",
          ends_at: c.ends_at ? dayjs(c.ends_at).format("YYYY-MM-DDTHH:mm") : "",
          usage_limit: c.usage_limit ?? "",
          usage_per_user_limit: c.usage_per_user_limit ?? "",
          min_subtotal: c.min_subtotal ?? "",
          is_active: !!c.is_active,
          notes: c.notes ?? "",
          product_ids: c.product_ids ?? [],
          category_ids: c.category_ids ?? [],
        });
      } else {
        // reset for create
        setForm({
          code: "",
          discount_type: "percentage",
          discount_value: 10,
          max_discount: "",
          channel: "online",
          applies_to: "all",
          starts_at: "",
          ends_at: "",
          usage_limit: "",
          usage_per_user_limit: "",
          min_subtotal: "",
          is_active: true,
          notes: "",
          product_ids: [],
          category_ids: [],
        });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to load coupon data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, couponId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        code: String(form.code || "").trim(),
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        max_discount:
          form.discount_type === "percentage" && form.max_discount !== ""
            ? Number(form.max_discount)
            : null,
        channel: form.channel,
        applies_to: form.applies_to,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
        usage_per_user_limit:
          form.usage_per_user_limit === "" ? null : Number(form.usage_per_user_limit),
        min_subtotal: form.min_subtotal === "" ? null : Number(form.min_subtotal),
        is_active: !!form.is_active,
        notes: form.notes || null,
        product_ids: form.applies_to === "products" ? form.product_ids : [],
        category_ids: form.applies_to === "categories" ? form.category_ids : [],
      };

      if (isEdit && couponId) {
        await updateAdminCoupon(couponId, payload);
      } else {
        await createAdminCoupon(payload);
      }

      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to save coupon");
    } finally {
      setSaving(false);
    }
  };

  const needsProducts = form.applies_to === "products";
  const needsCategories = form.applies_to === "categories";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Coupon" : "New Coupon"}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Stack py={6} alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {error && <Typography color="error">{error}</Typography>}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Code"
                value={form.code}
                onChange={(e) => setForm((s: any) => ({ ...s, code: e.target.value }))}
                fullWidth
                helperText="Customer enters this at checkout (case-insensitive)."
              />

              <FormControl fullWidth>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  label="Discount Type"
                  value={form.discount_type}
                  onChange={(e) =>
                    setForm((s: any) => ({
                      ...s,
                      discount_type: e.target.value,
                      max_discount: e.target.value === "percentage" ? s.max_discount : "",
                    }))
                  }
                >
                  <MenuItem value="fixed">Fixed Amount ($)</MenuItem>
                  <MenuItem value="percentage">Percentage Off (%)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label={form.discount_type === "percentage" ? "Discount Percent" : "Discount Amount"}
                type="number"
                value={form.discount_value}
                onChange={(e) => setForm((s: any) => ({ ...s, discount_value: e.target.value }))}
                fullWidth
              />
            </Stack>

            {form.discount_type === "percentage" && (
              <TextField
                label="Max Discount (optional)"
                type="number"
                value={form.max_discount}
                onChange={(e) => setForm((s: any) => ({ ...s, max_discount: e.target.value }))}
                fullWidth
                helperText="Optional cap for percentage discounts. Example: 20% off up to $25."
              />
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Min Subtotal (optional)"
                type="number"
                value={form.min_subtotal}
                onChange={(e) => setForm((s: any) => ({ ...s, min_subtotal: e.target.value }))}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  label="Channel"
                  value={form.channel}
                  onChange={(e) => setForm((s: any) => ({ ...s, channel: e.target.value }))}
                >
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="pos">POS</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Divider />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Applies To</InputLabel>
                <Select
                  label="Applies To"
                  value={form.applies_to}
                  onChange={(e) =>
                    setForm((s: any) => ({
                      ...s,
                      applies_to: e.target.value,
                      product_ids: e.target.value === "products" ? s.product_ids : [],
                      category_ids: e.target.value === "categories" ? s.category_ids : [],
                    }))
                  }
                >
                  <MenuItem value="all">All products</MenuItem>
                  <MenuItem value="products">Specific products</MenuItem>
                  <MenuItem value="categories">Specific categories</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Usage Limit (optional)"
                type="number"
                value={form.usage_limit}
                onChange={(e) => setForm((s: any) => ({ ...s, usage_limit: e.target.value }))}
                fullWidth
              />

              <TextField
                label="Per User Limit (optional)"
                type="number"
                value={form.usage_per_user_limit}
                onChange={(e) =>
                  setForm((s: any) => ({ ...s, usage_per_user_limit: e.target.value }))
                }
                fullWidth
              />
            </Stack>

            {needsProducts && (
              <Autocomplete
                multiple
                options={products}
                value={selectedProducts}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_, vals) =>
                  setForm((s: any) => ({ ...s, product_ids: vals.map((v) => v.id) }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Eligible Products" placeholder="Select products" />
                )}
              />
            )}

            {needsCategories && (
              <Autocomplete
                multiple
                options={categories}
                value={selectedCategories}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_, vals) =>
                  setForm((s: any) => ({ ...s, category_ids: vals.map((v) => v.id) }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Eligible Categories" placeholder="Select categories" />
                )}
              />
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Starts At (optional)"
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setForm((s: any) => ({ ...s, starts_at: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Ends At (optional)"
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm((s: any) => ({ ...s, ends_at: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              label="Notes (internal)"
              value={form.notes}
              onChange={(e) => setForm((s: any) => ({ ...s, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={!!form.is_active}
                onChange={(e) => setForm((s: any) => ({ ...s, is_active: e.target.checked }))}
              />
              <Typography>Active</Typography>
            </Stack>

            {(needsProducts && form.product_ids.length === 0) && (
              <Typography color="error">Select at least one product.</Typography>
            )}
            {(needsCategories && form.category_ids.length === 0) && (
              <Typography color="error">Select at least one category.</Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            saving ||
            !String(form.code || "").trim() ||
            !(Number(form.discount_value) > 0) ||
            (needsProducts && form.product_ids.length === 0) ||
            (needsCategories && form.category_ids.length === 0)
          }
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponDialog;